import { styled } from "@mui/material";
import Tab from "@mui/material/Tab";
import type { CSSProperties } from "react";
import { reactSelectSMStyle } from "Utils/Common";
import { formatUtcDateTimeInLabTimeZone } from "Utils/Common/LabTimeZone";
import { AutocompleteStyle } from "Utils/MuiStyles/AutocompleteStyles";
import type {
  IdLisRunDetailResponse,
  IdLisRunListResponse,
  IdLisRunSearchFilters,
} from "Services/InfectiousDisease/IdLisRunService";

export type RunGridRow = {
  id: string;
  idLISRunId: number;
  runName: string;
  runType: string;
  runTemplate: string;
  runDate: string;
  addedBy: string;
  plateBarcodeNo: string;
  runStatus?: string | null;
  lastRunStatus?: string | null;
  noOfAccession: number;
  /** One value per accession band (length === noOfAccession), left-to-right. */
  accessionSlots: string[];
  /** Applied accession labels, including the matched panel name(s), for plate headers. */
  accessionDisplaySlots: string[];
  rows: string[];
  columns: number[];
  rowCount: number;
  columnCount: number;
  totalWells: number;
  details: IdLisRunDetailResponse[];
};

export type AssignTestEntry = {
  label?: string | null;
  value?: string | null;
  testName?: string | null;
  name?: string | null;
  isControl?: boolean | null;
};

/** Lightweight lookup data for the Run Type and Run Template filters. */
export const RUNS_FILTER_LOOKUP_QUERY_KEY = "idlis-runs-filter-lookups";
export const RUN_DETAILS_QUERY_KEY = "idlis-run-details";

export const initialSearch: IdLisRunSearchFilters = {
  runId: "",
  runName: "",
  runType: "",
  runTemplate: "",
  runDate: "",
  addedBy: "",
  plateBarcodeNo: "",
};

export const RUN_FILTER_FIELDS: (keyof IdLisRunSearchFilters)[] = [
  "runId",
  "runName",
  "runType",
  "runTemplate",
  "runDate",
  "addedBy",
  "plateBarcodeNo",
];

export const RUN_FILTER_LABELS: Record<keyof IdLisRunSearchFilters, string> = {
  runId: "Run ID",
  runName: "Run Name",
  runType: "Run Type",
  runTemplate: "Run Template",
  runDate: "Run Date",
  addedBy: "Added By",
  plateBarcodeNo: "Plate Barcode No",
};

export const ID_LIS_MODULE_NAME = "ID LIS";
export const VIEW_CREATE_RUN_PERMISSION_PAGE = "View Create Run";
export const CREATE_RUNS_PERMISSION = "CreateRuns";
export const COMPLETE_RUNS_PERMISSION = "CompleteRuns";
export const DELETE_RUN_PERMISSION = "DeleteRun";
export const PRINT_PLATES_PERMISSION = "PrintPlates";
export const VIEW_RUNS_TAB_PERMISSION = "ViewRunsTab";
export const VIEW_COMPLETED_RUNS_PERMISSION = "ViewCompletedRuns";
export const VIEW_DELETED_RUNS_TAB_PERMISSION = "ViewDeletedRunsTab";

export type RunTabPermissionConfig = {
  value: number;
  label: string;
  permissionIdentifier: string;
  tabId: string;
};

export const RUN_TAB_CONFIG: RunTabPermissionConfig[] = [
  {
    value: 0,
    label: "View Runs",
    permissionIdentifier: VIEW_RUNS_TAB_PERMISSION,
    tabId: "IDViewRunTabViewRuns",
  },
  {
    value: 1,
    label: "Complete Runs",
    permissionIdentifier: VIEW_COMPLETED_RUNS_PERMISSION,
    tabId: "IDViewRunTabCompleteRuns",
  },
  {
    value: 2,
    label: "Deleted Runs",
    permissionIdentifier: VIEW_DELETED_RUNS_TAB_PERMISSION,
    tabId: "IDViewRunTabDeletedRuns",
  },
];

export const normalizePermissionValue = (value?: string | null) =>
  String(value ?? "")
    .replace(/\\n/g, "")
    .trim()
    .toLowerCase();

// ── Styles ─────────────────────────────────────────────────────────────────────

export const inputStyles: CSSProperties = {
  border: "1px solid #D3D3D3",
  borderRadius: "16px",
  padding: "14px 16px",
  height: "38px",
  fontSize: "14px",
  color: "#3F4254",
  boxShadow: "none",
  width: "100%",
};

export const fullWidthSelectStyles = {
  ...reactSelectSMStyle,
  control: (base: any) => ({
    ...(reactSelectSMStyle.control
      ? (reactSelectSMStyle.control as (base: any) => any)(base)
      : base),
    width: "100%",
    minWidth: "100%",
  }),
};

export const accessionPanelStyle: CSSProperties = {
  border: "2px solid #C084FC",
  borderRadius: "10px",
  padding: "20px",
  background: "#faf5ff",
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};

export const accessionPanelTitleStyle: CSSProperties = {
  color: "#7C3AED",
  fontWeight: 600,
  fontSize: "15px",
  marginBottom: "16px",
};

export const plateGridWrapperStyle: CSSProperties = {
  marginTop: "18px",
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  overflowX: "auto",
  contain: "inline-size",
  border: "1px solid #E1E3EA",
  borderRadius: "8px",
  background: "#FFFFFF",
};

export const plateGridTableStyle: CSSProperties = {
  borderCollapse: "separate",
  borderSpacing: 0,
  minWidth: "100%",
};

export const plateHeaderCellStyle: CSSProperties = {
  minWidth: "120px",
  padding: "7px 8px",
  background: "#EEF0F7",
  borderRight: "1px solid #D8DDEB",
  borderBottom: "1px solid #D8DDEB",
  color: "#070910",
  fontSize: "13px",
  fontWeight: 600,
  textAlign: "center",
  whiteSpace: "nowrap",
};

export const plateRowHeaderCellStyle: CSSProperties = {
  width: "38px",
  minWidth: "38px",
  padding: "7px 8px",
  background: "#F7F8FC",
  borderRight: "1px solid #D8DDEB",
  borderBottom: "1px solid #D8DDEB",
  color: "#3F4254",
  fontSize: "13px",
  fontWeight: 600,
  textAlign: "center",
};

export const plateWellCellStyle: CSSProperties = {
  minWidth: "120px",
  maxWidth: "160px",
  height: "76px",
  padding: "8px",
  borderRight: "1px solid #D8DDEB",
  borderBottom: "1px solid #D8DDEB",
  color: "#3F4254",
  fontSize: "13px",
  lineHeight: "18px",
  verticalAlign: "top",
  background: "#FFFFFF",
};

export const TabSelected = styled(Tab)(AutocompleteStyle());

export function a11yProps(tabValue: number) {
  const tabId =
    RUN_TAB_CONFIG.find((tab) => tab.value === tabValue)?.tabId ||
    `IDViewRunTab${tabValue}`;
  return {
    id: tabId,
    "aria-controls": `id-view-create-run-tabpanel-${tabValue}`,
  };
}

export const formatRunDate = (dateText?: string | null) => {
  if (!dateText) return "";

  const parsedDate = new Date(dateText);

  if (Number.isNaN(parsedDate.getTime())) return dateText;

  return parsedDate.toLocaleDateString();
};

export const formatPlateTemplate = (run: IdLisRunListResponse) => {
  if (run.plateTemplate) return run.plateTemplate;
  if (run.outputTemplate) return run.outputTemplate;
  if (run.plateTypeId) return `${run.plateTypeId} Well`;
  return "";
};

export const getRunAccessions = (details: IdLisRunDetailResponse[] = []) => {
  const uniqueAccessions = new Set<string>();

  details.forEach((detail) => {
    const accession = detail.accessionNumber?.trim();
    if (accession) uniqueAccessions.add(accession);
  });

  return Array.from(uniqueAccessions);
};

export function parseNoOfAccession(
  value: string | number | null | undefined
): number {
  const n = parseInt(String(value ?? "1"), 10);
  if (!Number.isFinite(n) || n < 1) return 1;
  return n;
}

export function usesTwoAccessionsPerColumn(
  rowCount: number,
  columnCount: number,
  accessionCount: number
) {
  const safeRows = Math.max(0, rowCount);
  const safeColumns = Math.max(0, columnCount);
  const safeAccessions = Math.max(1, accessionCount);
  return (
    safeRows > 0 &&
    safeRows % 2 === 0 &&
    safeColumns > 0 &&
    safeAccessions === safeColumns * 2
  );
}

/** Column counts per accession band (left to right); remainder goes to first slots. */
export function getColumnSpansPerAccession(
  columnCount: number,
  accessionCount: number
) {
  const n = Math.max(1, accessionCount);
  const total = Math.max(1, columnCount);
  const base = Math.floor(total / n);
  const rem = total % n;
  return Array.from({ length: n }, (_, i) => base + (i < rem ? 1 : 0));
}

export function accessionBandIndexForColumn(
  columnNumber: number,
  columnCount: number,
  accessionCount: number
): number {
  const spans = getColumnSpansPerAccession(columnCount, accessionCount);
  let start = 1;
  for (let i = 0; i < spans.length; i++) {
    const end = start + spans[i] - 1;
    if (columnNumber >= start && columnNumber <= end) return i;
    start = end + 1;
  }
  return Math.max(0, spans.length - 1);
}

export function getDetailColumnNumber(
  detail: IdLisRunDetailResponse
): number | null {
  if (
    detail.wellNumber !== null &&
    detail.wellNumber !== undefined &&
    Number.isFinite(detail.wellNumber)
  ) {
    const n = Number(detail.wellNumber);
    if (n > 0) return n;
  }

  const source = detail.wellPosition ?? detail.wellName;
  if (!source) return null;

  const match = source.match(/(\d+)/);
  if (!match) return null;

  const parsed = parseInt(match[1], 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function getDetailRowIndex(
  detail: IdLisRunDetailResponse
): number | null {
  const source = detail.wellAlphabets ?? detail.wellPosition ?? detail.wellName;
  if (!source) return null;

  const match = source
    .trim()
    .toUpperCase()
    .match(/^([A-Z])/);
  if (!match) return null;

  return match[1].charCodeAt(0) - "A".charCodeAt(0);
}

export function accessionSlotIndexForWell(
  detail: IdLisRunDetailResponse,
  rowCount: number,
  columnCount: number,
  accessionCount: number
) {
  const column = getDetailColumnNumber(detail);
  if (column === null || column < 1 || column > columnCount) return null;

  if (usesTwoAccessionsPerColumn(rowCount, columnCount, accessionCount)) {
    const rowIndex = getDetailRowIndex(detail);
    if (rowIndex === null || rowIndex < 0 || rowIndex >= rowCount) return null;
    return (column - 1) * 2 + (rowIndex % 2 === 0 ? 0 : 1);
  }

  return accessionBandIndexForColumn(column, columnCount, accessionCount);
}

export function buildAccessionSlotsFromDetails(
  details: IdLisRunDetailResponse[],
  accessionCount: number,
  rowCount: number,
  columnCount: number
): string[] {
  const slots = Array.from({ length: accessionCount }, () => "");
  const seenPerSlot = slots.map(() => new Set<string>());

  details.forEach((detail) => {
    const slotIndex = accessionSlotIndexForWell(
      detail,
      rowCount,
      columnCount,
      accessionCount
    );
    if (slotIndex === null || slotIndex < 0 || slotIndex >= accessionCount)
      return;

    const acc = detail.accessionNumber?.trim();
    if (!acc) return;
    if (seenPerSlot[slotIndex].has(acc)) return;
    seenPerSlot[slotIndex].add(acc);
    slots[slotIndex] = slots[slotIndex] ? `${slots[slotIndex]}, ${acc}` : acc;
  });

  if (slots.every((s) => !s)) {
    const legacy = getRunAccessions(details);
    for (let i = 0; i < accessionCount && i < legacy.length; i++) {
      slots[i] = legacy[i];
    }
  }

  return slots;
}

export function buildAccessionDisplaySlotsFromDetails(
  details: IdLisRunDetailResponse[],
  accessionCount: number,
  rowCount: number,
  columnCount: number
): string[] {
  const slots = Array.from({ length: accessionCount }, () => "");
  const seenPerSlot = slots.map(() => new Set<string>());

  details.forEach((detail) => {
    const slotIndex = accessionSlotIndexForWell(
      detail,
      rowCount,
      columnCount,
      accessionCount
    );
    if (slotIndex === null || slotIndex < 0 || slotIndex >= accessionCount)
      return;

    const accession = detail.accessionNumber?.trim();
    if (!accession) return;

    const panelName = detail.panelName?.trim();
    const displayValue = panelName ? `${accession} (${panelName})` : accession;
    if (seenPerSlot[slotIndex].has(displayValue)) return;

    seenPerSlot[slotIndex].add(displayValue);
    slots[slotIndex] = slots[slotIndex]
      ? `${slots[slotIndex]}, ${displayValue}`
      : displayValue;
  });

  return slots;
}

export const mapApiRunToGridRow = (
  run: IdLisRunListResponse,
  labTimeZone?: string | null
): RunGridRow => {
  const details = run.details ?? [];
  const runDateValue = run.runDate || run.createdDate;
  const runTemplate = formatPlateTemplate(run);
  const rows = run.rows?.length ? run.rows : [];
  const columns = run.columns?.length ? run.columns : [];
  const rowCount =
    run.rowCount > 0 ? run.rowCount : rows.length > 0 ? rows.length : 8;
  const columnCount =
    run.columnCount > 0
      ? run.columnCount
      : columns.length > 0
        ? columns.length
        : 12;
  const noOfAccession = Math.min(
    parseNoOfAccession(run.noOfAccession),
    Math.max(1, rowCount * columnCount)
  );
  const accessionSlots = buildAccessionSlotsFromDetails(
    details,
    noOfAccession,
    rowCount,
    columnCount
  );
  const accessionDisplaySlots = buildAccessionDisplaySlotsFromDetails(
    details,
    noOfAccession,
    rowCount,
    columnCount
  );

  return {
    id: run.runID || String(run.idLISRunId),
    idLISRunId: run.idLISRunId,
    runName: run.runName || "",
    runType: run.outputTemplate || "",
    runTemplate,
    runDate:
      formatUtcDateTimeInLabTimeZone(runDateValue, labTimeZone, "MM/dd/yyyy") ??
      formatRunDate(runDateValue),
    addedBy: run.createdBy || "",
    plateBarcodeNo: run.runBarcodeNo || "",
    runStatus: run.runStatus,
    lastRunStatus: run.lastRunStatus,
    noOfAccession,
    accessionSlots,
    accessionDisplaySlots,
    rows: run.rows ?? [],
    columns: run.columns ?? [],
    rowCount,
    columnCount,
    totalWells: run.totalWells,
    details,
  };
};

export const getUniqueOptions = (values: string[]) => {
  return Array.from(
    new Set(values.map((value) => value.trim()).filter(Boolean))
  );
};

export const normalizeWellKey = (value?: string | null) => {
  return value ? value.replace(/\s+/g, "").trim().toUpperCase() : "";
};

export const getDetailWellKey = (detail: IdLisRunDetailResponse) => {
  if (detail.wellAlphabets && detail.wellNumber) {
    return normalizeWellKey(`${detail.wellAlphabets}${detail.wellNumber}`);
  }

  return "";
};

export const buildWellLookup = (details: IdLisRunDetailResponse[] = []) => {
  const lookup = new Map<string, IdLisRunDetailResponse>();

  details.forEach((detail) => {
    [detail.wellPosition, detail.wellName, getDetailWellKey(detail)].forEach(
      (key) => {
        const normalizedKey = normalizeWellKey(key);
        if (normalizedKey && !lookup.has(normalizedKey)) {
          lookup.set(normalizedKey, detail);
        }
      }
    );
  });

  return lookup;
};

export const parseAssignedTestLabels = (assignTestJson?: string | null) => {
  if (!assignTestJson) return [];

  try {
    const parsed = JSON.parse(assignTestJson);
    const entries: AssignTestEntry[] = Array.isArray(parsed)
      ? parsed
      : Array.isArray(parsed?.entries)
        ? parsed.entries
        : [];

    return entries
      .map(
        (entry) => entry.label || entry.testName || entry.name || entry.value
      )
      .filter((label): label is string => Boolean(label));
  } catch {
    return [];
  }
};

/** Cell text: control entries use `controlLabel`; preserves order, de-dupes labels. */
// const formatAssignTestCellText = (
//   assignTestJson: string | null | undefined,
//   controlLabel: string
// ) => {
//   if (!assignTestJson) return "";

//   try {
//     const parsed = JSON.parse(assignTestJson);
//     const entries: AssignTestEntry[] = Array.isArray(parsed)
//       ? parsed
//       : Array.isArray(parsed?.entries)
//         ? parsed.entries
//         : [];

//     const parts: string[] = [];
//     const seen = new Set<string>();

//     for (const entry of entries) {
//       const piece = entry.isControl
//         ? controlLabel
//         : String(
//           entry.label || entry.testName || entry.name || entry.value || ""
//         ).trim();
//       if (!piece || seen.has(piece)) continue;
//       seen.add(piece);
//       parts.push(piece);
//     }

//     return parts.join(", ");
//   } catch {
//     return parseAssignedTestLabels(assignTestJson).join(", ");
//   }
// };

// ── Component ──────────────────────────────────────────────────────────────────

// V2
export const formatAssignTestCellText = (
  assignTestJson: string | null | undefined
) => {
  if (!assignTestJson) return "";

  try {
    const parsed = JSON.parse(assignTestJson);
    const entries: AssignTestEntry[] = Array.isArray(parsed)
      ? parsed
      : Array.isArray(parsed?.entries)
        ? parsed.entries
        : [];

    const parts: string[] = [];
    const seen = new Set<string>();

    for (const entry of entries) {
      const piece = String(
        entry.label || entry.testName || entry.name || entry.value || ""
      ).trim();
      if (!piece || seen.has(piece)) continue;
      seen.add(piece);
      parts.push(piece);
    }

    return parts.join(", ");
  } catch {
    return parseAssignedTestLabels(assignTestJson).join(", ");
  }
};

export const DUPLICATE_ACCESSION_ERROR_TEXT = "Accession already exists.";

export const buildDuplicateAccessionErrors = (
  accessionSlots: string[],
  errorText: string
) => {
  const firstIndexByValue = new Map<string, number>();
  const duplicateErrors: Record<number, string> = {};

  accessionSlots.forEach((rawValue, index) => {
    const trimmed = String(rawValue ?? "").trim();
    if (!trimmed) return;

    const key = trimmed.toUpperCase();
    const firstIndex = firstIndexByValue.get(key);

    if (firstIndex === undefined) {
      firstIndexByValue.set(key, index);
      return;
    }

    duplicateErrors[firstIndex] = errorText;
    duplicateErrors[index] = errorText;
  });

  return duplicateErrors;
};
